import React from 'react';
import { Box, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import ThemeToggle from './ThemeToggle';

interface BookContainerProps {
  children: React.ReactNode;
}

const BookContainer: React.FC<BookContainerProps> = ({ 
  children
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start', // Prevent top clipping when content overflows
        background: theme.palette.mode === 'dark'
          ? 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)'
          : 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)',
        p: { xs: 1, sm: 2, md: 3, lg: 4 }, // Reduced padding on md (tablets) for better reflow
        pt: { xs: 2, md: 4 },
        pb: { xs: 2, md: 4 }, // Ensure bottom padding for scrolling
        position: 'relative',
        overflow: 'auto', // Always allow scrolling if content is too tall
      }}
    >
      {/* Background texture for depth */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.03,
          backgroundImage: theme.palette.mode === 'dark'
            ? 'radial-gradient(circle at 25% 25%, rgba(255,255,255,0.1) 0%, transparent 50%)'
            : 'radial-gradient(circle at 25% 25%, rgba(0,0,0,0.05) 0%, transparent 50%)',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />

      {/* Main book container */}
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.8, type: 'spring', damping: 25 }}
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: '1200px',
          zIndex: 1,
          margin: 'auto', // Centers vertically when space allows, keeps top-aligned when scrolling
        }}
      >
        {/* Book shadow */}
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            left: 8,
            right: -8,
            bottom: -8,
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.1) 100%)'
              : 'linear-gradient(135deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.05) 100%)',
            borderRadius: '12px',
            filter: 'blur(8px)',
            zIndex: -1,
          }}
        />

        {/* Book content */}
        <Box
          sx={{
            position: 'relative',
            borderRadius: '12px',
            overflow: 'hidden',
            background: theme.palette.mode === 'dark' 
              ? 'linear-gradient(180deg, rgba(46, 33, 23, 0.95) 0%, rgba(32, 22, 16, 0.98) 100%)'
              : '#FFFEF8',
            border: theme.palette.mode === 'dark'
              ? '3px solid rgba(255, 138, 101, 0.3)'
              : '3px solid rgba(139, 69, 19, 0.4)',
            boxShadow: theme.palette.mode === 'dark'
              ? 'inset 0 0 50px rgba(255, 138, 101, 0.1), 0 10px 40px rgba(0,0,0,0.5)'
              : 'inset 0 0 50px rgba(139, 69, 19, 0.1), 0 10px 40px rgba(139, 69, 19, 0.2)',
          }}
        >
          {/* Theme toggle - positioned in top-right corner of book */}
          <Box
            sx={{
              position: 'absolute',
              top: 15,
              right: 15,
              zIndex: 10,
            }}
          >
            <ThemeToggle />
          </Box>

          {children}
        </Box>
      </motion.div>
    </Box>
  );
};

export default BookContainer;
